import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { STLLoader } from "three/addons/loaders/STLLoader.js";
import ViewControlBar from "./ViewControlBar.jsx";
import {
  ORIGIN,
  applyCameraView,
  configurePreviewRenderer,
  createPreviewLights,
  fitDistanceForObject,
} from "../lib/threeFraming.js";

function centerGeometry(geometry) {
  geometry.computeBoundingBox();
  const center = new THREE.Vector3();
  geometry.boundingBox.getCenter(center);
  geometry.translate(-center.x, -center.y, -center.z);
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}

export default function StlViewer({ src, className = "" }) {
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const activeViewRef = useRef("iso");
  const [loading, setLoading] = useState(Boolean(src));
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState("iso");

  const handleView = useCallback((preset) => {
    activeViewRef.current = preset;
    setActiveView(preset);
    apiRef.current?.setView(preset);
  }, []);

  const handleFit = useCallback(() => {
    activeViewRef.current = "iso";
    setActiveView("iso");
    apiRef.current?.setView("iso");
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !src) return undefined;

    setLoading(true);
    setError(null);
    activeViewRef.current = "iso";
    setActiveView("iso");

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a0f);

    const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 1_000_000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    configurePreviewRenderer(renderer);
    container.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.screenSpacePanning = false;
    controls.target.copy(ORIGIN);

    createPreviewLights(scene);

    let mesh = null;
    let fitDistance = 100;
    let animId = null;
    let cancelled = false;

    const frameModel = (preset = "iso") => {
      if (!mesh) return;
      fitDistance = fitDistanceForObject(mesh, camera);
      controls.minDistance = fitDistance * 0.1;
      controls.maxDistance = fitDistance * 10;
      applyCameraView(camera, controls, mesh, preset, fitDistance);
    };

    apiRef.current = {
      setView: (preset) => frameModel(preset),
    };

    const resize = () => {
      const width = Math.max(container.clientWidth, 1);
      const height = Math.max(container.clientHeight, 1);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
      if (mesh) frameModel(activeViewRef.current);
    };

    const loader = new STLLoader();
    loader.load(
      src,
      (geometry) => {
        if (cancelled) {
          geometry.dispose();
          return;
        }

        centerGeometry(geometry);
        geometry.computeVertexNormals();

        const material = new THREE.MeshStandardMaterial({
          color: 0x7dd3fc,
          metalness: 0.12,
          roughness: 0.48,
        });
        mesh = new THREE.Mesh(geometry, material);
        mesh.position.set(0, 0, 0);
        scene.add(mesh);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (cancelled) return;
            resize();
            frameModel("iso");
            setLoading(false);
          });
        });
      },
      undefined,
      (err) => {
        if (cancelled) return;
        setLoading(false);
        setError(err?.message || "Could not load STL preview.");
      }
    );

    const animate = () => {
      animId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };

    resize();
    animate();

    const observer = new ResizeObserver(resize);
    observer.observe(container);

    return () => {
      cancelled = true;
      apiRef.current = null;
      cancelAnimationFrame(animId);
      observer.disconnect();
      controls.dispose();
      if (mesh) {
        mesh.geometry.dispose();
        mesh.material.dispose();
      }
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [src]);

  if (!src) {
    return (
      <div
        className={`flex h-[420px] items-center justify-center rounded-xl border border-dashed border-white/15 bg-white/[0.02] text-sm text-white/45 ${className}`}
      >
        Your generated model preview will appear here.
      </div>
    );
  }

  return (
    <div
      className={`relative h-[420px] w-full overflow-hidden rounded-xl border border-white/10 bg-black/30 ${className}`}
      style={{
        background:
          "radial-gradient(circle at 50% 35%, rgba(56,189,248,0.08), transparent 55%)",
      }}
    >
      <div ref={containerRef} className="absolute inset-0 h-full w-full" />
      {!loading && !error && (
        <ViewControlBar activeView={activeView} onSelect={handleView} onFit={handleFit} />
      )}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/20 text-sm text-white/55">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading preview…
        </div>
      )}
      {error && (
        <div className="absolute inset-x-4 bottom-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}
    </div>
  );
}
